/**
 * Channel Conversation Service
 *
 * Manages the mapping between external channel conversations (e.g., WhatsApp via ChatSuite)
 * and Flow's unified inbox conversations.
 */
import type { SupabaseClient } from '@supabase/supabase-js';

import { Result, ServiceError } from '@kit/shared/result';
import { BaseService } from '@kit/shared/services';
import type { Database } from '@kit/supabase/database';

const SYSTEM_USER_ID = '00000000-0000-0000-0000-000000000000';

export interface ChannelConversationMapping {
  id: string;
  conversation_id: string;
  channel_type: string;
  provider_contact_id: string;
  provider_workspace_id: string;
  external_contact_id: string;
  phone_number: string;
  contact_name: string | null;
  last_synced_at: string | null;
  created_at: string;
  updated_at: string;
}

export interface CreateMappingParams {
  conversationId: string;
  channelType: string;
  providerContactId: string;
  providerWorkspaceId: string;
  externalContactId: string;
  phoneNumber: string;
  contactName?: string;
}

export interface FindOrCreateParams {
  externalContactId: string;
  phoneNumber: string;
  channelType: string;
  providerContactId: string;
  providerWorkspaceId: string;
  contactName?: string;
}

/**
 * ChannelConversationService
 *
 * Handles the bidirectional mapping between external channel contacts
 * and Flow conversations for the unified inbox experience.
 */
export class ChannelConversationService extends BaseService {
  constructor(client: SupabaseClient<Database>) {
    super(client);
  }

  /**
   * Get channel mapping by conversation ID
   * Returns null if conversation has no channel mapping
   */
  async getChannelMapping(
    conversationId: string,
  ): Promise<Result<ChannelConversationMapping | null>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { data, error } = await this.client
      .from('channel_conversation_mapping')
      .select('*')
      .eq('conversation_id', conversationId)
      .maybeSingle();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(data as ChannelConversationMapping | null);
  }

  /**
   * Get channel mapping by external contact ID and workspace
   */
  async getByExternalContactId(
    externalContactId: string,
    providerWorkspaceId: string,
  ): Promise<Result<ChannelConversationMapping | null>> {
    if (!externalContactId || !providerWorkspaceId) {
      return Result.fail(
        new ServiceError(
          'VALIDATION_ERROR',
          'externalContactId and providerWorkspaceId are required',
        ),
      );
    }

    const { data, error } = await this.client
      .from('channel_conversation_mapping')
      .select('*')
      .eq('external_contact_id', externalContactId)
      .eq('provider_workspace_id', providerWorkspaceId)
      .maybeSingle();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(data as ChannelConversationMapping | null);
  }

  /**
   * Create a new channel conversation mapping
   */
  async createMapping(
    params: CreateMappingParams,
  ): Promise<Result<ChannelConversationMapping>> {
    const {
      conversationId,
      channelType,
      providerContactId,
      providerWorkspaceId,
      externalContactId,
      phoneNumber,
      contactName,
    } = params;

    // Validate conversation ID
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    // Validate required fields
    if (!providerContactId || !providerWorkspaceId || !externalContactId) {
      return Result.fail(
        new ServiceError(
          'VALIDATION_ERROR',
          'Provider contact ID, workspace ID, and external contact ID are required',
        ),
      );
    }

    if (!phoneNumber) {
      return Result.fail(
        new ServiceError('VALIDATION_ERROR', 'Phone number is required'),
      );
    }

    const { data, error } = await this.client
      .from('channel_conversation_mapping')
      .insert({
        conversation_id: conversationId,
        channel_type: channelType,
        provider_contact_id: providerContactId,
        provider_workspace_id: providerWorkspaceId,
        external_contact_id: externalContactId,
        phone_number: phoneNumber,
        contact_name: contactName || null,
      })
      .select()
      .single();

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(data as ChannelConversationMapping);
  }

  /**
   * Find an existing conversation mapping or create a new one with a new conversation.
   * This is the main entry point for inbound channel messages.
   *
   * Creates a new Flow conversation if no mapping exists.
   */
  async findOrCreateConversation(
    params: FindOrCreateParams,
  ): Promise<Result<{ conversationId: string; isNew: boolean }>> {
    const {
      externalContactId,
      phoneNumber,
      channelType,
      providerContactId,
      providerWorkspaceId,
      contactName,
    } = params;

    // Validate required fields
    if (!externalContactId || !providerWorkspaceId) {
      return Result.fail(
        new ServiceError(
          'VALIDATION_ERROR',
          'External contact ID and workspace ID are required',
        ),
      );
    }

    // Check if mapping already exists
    const existingResult = await this.getByExternalContactId(
      externalContactId,
      providerWorkspaceId,
    );
    if (existingResult.isFailure) {
      return Result.fail(existingResult.getError());
    }

    const existing = existingResult.getValue();
    if (existing) {
      // Update contact name if provided and different
      if (contactName && contactName !== existing.contact_name) {
        await this.client
          .from('channel_conversation_mapping')
          .update({ contact_name: contactName })
          .eq('id', existing.id);
      }

      return Result.ok({
        conversationId: existing.conversation_id,
        isNew: false,
      });
    }

    // Create new conversation for this channel contact
    // Use type 'group' for channel conversations (acts as external channel)
    const displayName = contactName || phoneNumber || externalContactId;
    const channelLabel =
      channelType.charAt(0).toUpperCase() + channelType.slice(1);

    const { data: conversation, error: convError } = await this.client
      .from('conversations')
      .insert({
        type: 'group',
        name: `${channelLabel}: ${displayName}`,
        description: `${channelLabel} conversation with ${phoneNumber}`,
        created_by: SYSTEM_USER_ID, // System user
        metadata: {
          channel: channelType,
          external_contact_id: externalContactId,
          phone_number: phoneNumber,
        },
      })
      .select()
      .single();

    if (convError) {
      return Result.fail(this.handleDatabaseError(convError));
    }

    // Create the mapping
    const mappingResult = await this.createMapping({
      conversationId: conversation.id,
      channelType,
      providerContactId,
      providerWorkspaceId,
      externalContactId,
      phoneNumber,
      contactName,
    });

    if (mappingResult.isFailure) {
      // Rollback conversation creation
      await this.client
        .from('conversations')
        .delete()
        .eq('id', conversation.id);
      return Result.fail(mappingResult.getError());
    }

    return Result.ok({
      conversationId: conversation.id,
      isNew: true,
    });
  }

  /**
   * Update the last synced timestamp for a mapping
   */
  async updateLastSynced(conversationId: string): Promise<Result<void>> {
    const uuidValidation = this.validateUUID(conversationId, 'conversationId');
    if (uuidValidation.isFailure) {
      return Result.fail(uuidValidation.getError());
    }

    const { error } = await this.client
      .from('channel_conversation_mapping')
      .update({ last_synced_at: new Date().toISOString() })
      .eq('conversation_id', conversationId);

    if (error) {
      return Result.fail(this.handleDatabaseError(error));
    }

    return Result.ok(undefined);
  }

  /**
   * Check if a conversation is linked to a channel
   */
  async isChannelLinked(conversationId: string): Promise<Result<boolean>> {
    const result = await this.getChannelMapping(conversationId);
    if (result.isFailure) {
      return Result.fail(result.getError());
    }

    return Result.ok(result.getValue() !== null);
  }
}
